import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/shadcn/lib/utils";
import { ReactComponent as ChevronLeftIcon } from "atoms/icons/chevron_left.svg";
import { ReactComponent as ChevronRightIcon } from "atoms/icons/chevron_right.svg";
import { type Tag, TagList } from "@/newComponents/TagList";

type ScrollableFilterTagsProps = {
  tags: Tag[];
  disabled?: boolean;
  ariaLabel: string;
  scrollLeftLabel: string;
  scrollRightLabel: string;
  onTagRemove: (tag: Tag) => void;
};

export const ScrollableFilterTags = ({
  tags,
  disabled,
  ariaLabel,
  scrollLeftLabel,
  scrollRightLabel,
  onTagRemove,
}: ScrollableFilterTagsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const hasOverflow = canScrollLeft || canScrollRight;
  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    setCanScrollLeft(element.scrollLeft > 0);
    setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
  }, []);
  useEffect(() => {
    updateScrollState();
    const element = scrollRef.current;
    if (!element) return;
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(element);
    return () => observer.disconnect();
  }, [tags, updateScrollState]);
  if (tags.length === 0) return null;
  return (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      {hasOverflow && (
        <button
          aria-label={scrollLeftLabel}
          className={cn(
            "h-8 w-8 shrink-0 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
            !canScrollLeft && "pointer-events-none opacity-30",
          )}
          disabled={!canScrollLeft || disabled}
          type="button"
          onClick={() => scrollRef.current?.scrollBy({ left: -140, behavior: "smooth" })}
        >
          <ChevronLeftIcon className="mx-auto h-4 w-4" />
        </button>
      )}
      <div
        className="no-scrollbar min-w-0 flex-1 overflow-x-auto overflow-y-hidden"
        ref={scrollRef}
        onScroll={updateScrollState}
      >
        <TagList
          aria-label={ariaLabel}
          className="w-max flex-nowrap"
          disabled={disabled}
          tags={tags}
          onTagRemove={onTagRemove}
        />
      </div>
      {hasOverflow && (
        <button
          aria-label={scrollRightLabel}
          className={cn(
            "h-8 w-8 shrink-0 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
            !canScrollRight && "pointer-events-none opacity-30",
          )}
          disabled={!canScrollRight || disabled}
          type="button"
          onClick={() => scrollRef.current?.scrollBy({ left: 140, behavior: "smooth" })}
        >
          <ChevronRightIcon className="mx-auto h-4 w-4" />
        </button>
      )}
    </div>
  );
};
